import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Pause, Play, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useNowPlaying, toggleTrack, stopTrack, SHARED_PLAYER_GUARD } from "@/lib/nowPlaying";
import { useGuard } from "@/lib/guards";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const fmt = (s) => { if (!Number.isFinite(s) || s < 0) s = 0; return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`; };

// Lot 685 — mini-lecteur flottant : visible SEULEMENT quand la publication SON en cours n'est plus à l'écran
// (défilement, carte démontée par le fil allégé). ⏯ / ✕ (arrêt) ; un tap sur le titre ramène à la publication
// (défilement si elle est dans la liste, sinon sa page). Masqué sur les Reels et dans un direct (le son y est déjà
// coupé par « un seul son à la fois »). Posé au-dessus de la barre du bas, sous les bandeaux de mise à jour.
export const NowPlayingBar = () => {
  const { t } = useApp();
  const np = useNowPlaying();
  const enabled = useGuard(SHARED_PLAYER_GUARD);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const hiddenRoute = pathname.startsWith("/live") || pathname.startsWith("/reels");
  // Garde-fou coupé pendant une lecture → le son partagé s'arrête (les cartes repassent au lecteur local).
  useEffect(() => { if (!enabled && np.track) stopTrack(); }, [enabled, np.track]);
  if (!enabled || !np.track || np.cardVisible || np.ended || hiddenRoute) return null;
  const { track } = np;
  const pct = np.dur > 0 ? Math.min(100, (np.cur / np.dur) * 100) : 0;
  const goTo = () => {
    // Carte montée (post-<id>) ou déchargée par le fil allégé (son bloc feed-window-<id> garde la hauteur) → défilement ;
    // la carte se remonte en arrivant à l'écran et la barre s'efface. Sinon (autre écran) → page de la publication.
    const node = document.querySelector(`[data-testid="post-${track.id}"]`) || document.querySelector(`[data-testid="feed-window-${track.id}"]`);
    if (node) node.scrollIntoView({ block: "center", behavior: "smooth" });
    else nav(`/post/${track.id}`);
  };
  return (
    <div className="fixed left-0 right-0 z-[45] px-2 pointer-events-none" style={{ bottom: "calc(3.5rem + var(--sa-bottom, 0px) + 0.5rem)" }} data-testid="now-playing-bar" data-playing={np.playing ? "1" : "0"}>
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl shadow-lg border overflow-hidden" style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }}>
        <div className="h-[3px] w-full" style={{ background: "var(--yalola-border)" }}>
          <div className="h-full" style={{ width: `${pct}%`, background: "var(--yalola-primary)", transition: "width 250ms linear" }} data-testid="now-playing-progress" />
        </div>
        <div className="flex items-center gap-2.5 pl-2 pr-1.5 py-1.5">
          <button
            type="button"
            onClick={() => toggleTrack(track)}
            className="h-9 w-9 rounded-full grid place-items-center shrink-0 text-white transition-transform active:scale-90"
            style={{ background: "var(--yalola-primary)" }}
            aria-label={np.playing ? t("chat.pause", "Pause") : t("chat.play", "Lecture")}
            data-testid="now-playing-toggle"
          >
            {np.playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
          </button>
          <button type="button" onClick={goTo} className="flex-1 min-w-0 flex items-center gap-2 text-left" data-testid="now-playing-open">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={track.avatar || undefined} />
              <AvatarFallback>{track.title?.[0]}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: "var(--yalola-text)" }} data-testid="now-playing-title">{track.title}</div>
              <div className="text-[11px] truncate tabular-nums" style={{ color: "var(--yalola-text-muted)" }} data-testid="now-playing-time">
                {fmt(np.cur)} / {fmt(np.dur)}{track.subtitle ? ` · ${track.subtitle}` : ""}
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={stopTrack}
            className="h-8 w-8 rounded-full grid place-items-center shrink-0"
            style={{ color: "var(--yalola-text-muted)" }}
            aria-label={t("audio.stop", "Arrêter")}
            data-testid="now-playing-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NowPlayingBar;
